import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CrimeMap from "@/components/vigilante/CrimeMap";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

type CrimeReport = Tables<"crime_reports">;

const CrimeMapPage = () => {
  const [showLast24Hours, setShowLast24Hours] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const isFromReport = location.state?.fromReport || false;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          toast({
            title: "Location Found",
            description: "Showing crimes near your location",
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          toast({
            title: "Location Error",
            description: "Could not get your location. Showing default map view.",
            variant: "destructive",
          });
        }
      );
    }
  }, [toast]);

  useEffect(() => {
    const channel = supabase
      .channel('public:crime_reports')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'crime_reports' 
      }, payload => {
        console.log('New crime report:', payload);
        queryClient.invalidateQueries({ queryKey: ["crimeReports"] });
        toast({
          title: "New Crime Report",
          description: "A new crime has been reported in your area"
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast]);

  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const { data: crimeReports, isLoading, error } = useQuery({
    queryKey: ["crimeReports"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crime_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CrimeReport[];
    },
  });

  const filteredReports = React.useMemo(() => {
    if (!crimeReports) return [];
    
    if (showLast24Hours) {
      return crimeReports.filter(
        report => new Date(report.created_at) > new Date(twentyFourHoursAgo)
      );
    }
    
    return crimeReports;
  }, [crimeReports, showLast24Hours, twentyFourHoursAgo]);

  useEffect(() => {
    if (isFromReport) {
      setShowLast24Hours(true);
    }
  }, [isFromReport]);

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Crime Map</h1>
          
          <div className="flex items-center space-x-2">
            <Switch 
              id="last24hours" 
              checked={showLast24Hours} 
              onCheckedChange={setShowLast24Hours} 
            />
            <Label htmlFor="last24hours" className="flex items-center cursor-pointer">
              <Calendar className="h-4 w-4 mr-2" />
              Last 24 Hours
            </Label>
          </div>
        </div>
        
        <Card className="mb-6">
          <CardContent className="p-0">
            <div className="h-[600px] relative">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                  <div className="text-center p-6">
                    <h3 className="text-lg font-semibold mb-2 text-destructive">Error Loading Data</h3>
                    <p>There was an error loading the crime reports. Please try again later.</p>
                  </div>
                </div>
              ) : (
                <CrimeMap 
                  crimeReports={filteredReports} 
                  showLast24Hours={showLast24Hours}
                  initialLocation={userLocation} 
                />
              )}
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default CrimeMapPage;
